import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Power, ShieldAlert, Play, Pause, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";

const ENTRY_OPS = [
  { value: "price_below", label: "Price below" },
  { value: "price_above", label: "Price above" },
  { value: "rsi_below", label: "RSI below (oversold)" },
  { value: "sma_cross_up", label: "Price crosses above SMA" },
];
const EXIT_OPS = [
  { value: "price_above", label: "Price above" },
  { value: "price_below", label: "Price below" },
  { value: "rsi_above", label: "RSI above (overbought)" },
  { value: "sma_cross_down", label: "Price crosses below SMA" },
];

export default function AutoTrade() {
  const [strategies, setStrategies] = useState([]);
  const [kill, setKill] = useState({ user_kill_switch: false, global_kill_switch: false });
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    name: "",
    symbol: "BTC",
    asset_type: "crypto",
    entry_op: "price_below",
    entry_value: "",
    exit_op: "price_above",
    exit_value: "",
    sizing_mode: "fixed_notional",
    sizing_value: "500",
    stop_loss_pct: "3",
  });

  const load = () => Promise.all([
    api.get("/auto/strategies").then(({ data }) => setStrategies(data)),
    api.get("/auto/kill-switch").then(({ data }) => setKill(data)),
    api.get("/auto/audit?limit=25").then(({ data }) => setAudit(data)),
  ]);

  useEffect(() => { load(); }, []);

  const toggleKill = async (enabled) => {
    try {
      await api.post("/auto/kill-switch", { enabled });
      toast[enabled ? "warning" : "success"](enabled ? "Kill switch ON — auto-execution paused." : "Kill switch OFF.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kill switch update failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name required");
    if (!form.entry_value || !form.exit_value) return toast.error("Entry + exit values required");
    setBusy(true);
    try {
      await api.post("/auto/strategies", {
        name: form.name,
        symbol: form.symbol.toUpperCase(),
        asset_type: form.asset_type,
        entry: { op: form.entry_op, value: parseFloat(form.entry_value) },
        exit: { op: form.exit_op, value: parseFloat(form.exit_value) },
        sizing: { mode: form.sizing_mode, value: parseFloat(form.sizing_value) },
        stop_loss_pct: parseFloat(form.stop_loss_pct),
        enabled: true,
      });
      toast.success("Strategy created — first evaluation within 60s.");
      setForm((f) => ({ ...f, name: "", entry_value: "", exit_value: "" }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (s) => {
    try {
      await api.patch(`/auto/strategies/${s._id}`, { enabled: !s.enabled });
      load();
    } catch (err) {
      toast.error("Toggle failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Audit trail is preserved.`)) return;
    try {
      await api.delete(`/auto/strategies/${s._id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const anyKill = kill.user_kill_switch || kill.global_kill_switch;

  return (
    <div className="space-y-8" data-testid="auto-root">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Automated execution</p>
        <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight mt-2 flex items-center gap-3">
          <Bot size={40} className="text-black" />
          Rules-based auto-trader
        </h1>
        <p className="text-gray-500 mt-3 max-w-2xl">
          Paper trading only. Every fire routes through the deterministic risk engine — max position, max daily loss, market hours, required stop loss.
          The AI Council is decision support: it can never bypass these rules.
        </p>
      </div>

      <Card className={`border rounded-2xl p-6 ${anyKill ? "border-red-300 bg-red-50/40" : "border-gray-200"}`} data-testid="kill-switch-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldAlert size={22} className={anyKill ? "text-red-600" : "text-gray-600"} />
            <div>
              <p className="font-medium">Kill switch</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Instantly pause auto-execution and reject all inbound signals.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {kill.global_kill_switch && <Badge className="bg-red-600 text-white">Global kill ON</Badge>}
            <Switch
              checked={kill.user_kill_switch}
              onCheckedChange={toggleKill}
              data-testid="user-kill-switch"
            />
            <span className="text-sm font-medium">
              {kill.user_kill_switch ? "PAUSED" : "Active"}
            </span>
          </div>
        </div>
      </Card>

      <Card className="border border-gray-200 rounded-2xl p-6" data-testid="new-strategy-card">
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-4">Create strategy</p>
        <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Name</Label>
            <Input data-testid="strategy-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. BTC dip buy" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Symbol</Label>
              <Input data-testid="strategy-symbol" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} />
            </div>
            <div>
              <Label>Asset</Label>
              <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                <SelectTrigger data-testid="strategy-asset"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="stock">Stock</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Entry rule</Label>
              <Select value={form.entry_op} onValueChange={(v) => setForm({ ...form, entry_op: v })}>
                <SelectTrigger data-testid="entry-op"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTRY_OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input data-testid="entry-value" value={form.entry_value} onChange={(e) => setForm({ ...form, entry_value: e.target.value })} type="number" step="any" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Exit rule</Label>
              <Select value={form.exit_op} onValueChange={(v) => setForm({ ...form, exit_op: v })}>
                <SelectTrigger data-testid="exit-op"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXIT_OPS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value</Label>
              <Input data-testid="exit-value" value={form.exit_value} onChange={(e) => setForm({ ...form, exit_value: e.target.value })} type="number" step="any" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Sizing</Label>
              <Select value={form.sizing_mode} onValueChange={(v) => setForm({ ...form, sizing_mode: v })}>
                <SelectTrigger data-testid="sizing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed_notional">Fixed $</SelectItem>
                  <SelectItem value="percent_portfolio">% of portfolio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Value ({form.sizing_mode === "fixed_notional" ? "$" : "%"})</Label>
              <Input data-testid="sizing-value" value={form.sizing_value} onChange={(e) => setForm({ ...form, sizing_value: e.target.value })} type="number" step="any" />
            </div>
          </div>
          <div>
            <Label>Stop loss %</Label>
            <Input data-testid="stop-loss-pct" value={form.stop_loss_pct} onChange={(e) => setForm({ ...form, stop_loss_pct: e.target.value })} type="number" step="0.1" />
            <p className="text-[10px] text-gray-500 mt-1">Required. Every strategy must have a hard stop.</p>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" disabled={busy} data-testid="create-strategy-btn" className="rounded-full bg-brand text-white h-11 px-6">
              {busy ? "Creating…" : "Create strategy"}
            </Button>
          </div>
        </form>
      </Card>

      <Card className="border border-gray-200 rounded-2xl overflow-hidden" data-testid="strategies-card">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="font-medium">Your strategies ({strategies.length})</p>
          <p className="text-xs text-gray-500">Evaluated every ~60s</p>
        </div>
        {strategies.length === 0 ? (
          <p className="text-sm text-gray-500 p-6">No strategies yet. Create one above.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {strategies.map((s) => (
              <div key={s._id} className="p-4 flex items-center gap-4" data-testid={`strategy-row-${s._id}`}>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{s.name}</p>
                    <Badge className={s.enabled ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}>
                      {s.enabled ? "ON" : "OFF"}
                    </Badge>
                    <span className="text-xs font-mono text-gray-500">{s.symbol}·{s.asset_type}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Entry: {s.entry.op} @ {s.entry.value} · Exit: {s.exit.op} @ {s.exit.value} · Stop {s.stop_loss_pct}%
                  </p>
                  {s.last_reason && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      Last: <span className="font-mono">{s.last_action}</span> — {s.last_reason}
                    </p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => toggle(s)} data-testid={`toggle-${s._id}`}>
                  {s.enabled ? <Pause size={16} /> : <Play size={16} />}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => del(s)} data-testid={`delete-${s._id}`}>
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border border-gray-200 rounded-2xl overflow-hidden" data-testid="audit-card">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Clock size={16} className="text-gray-500" />
          <p className="font-medium">Audit trail</p>
        </div>
        {audit.length === 0 ? (
          <p className="text-sm text-gray-500 p-6">No events yet.</p>
        ) : (
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {audit.map((a) => (
              <div key={a._id} className="px-6 py-3 text-xs flex items-center gap-3">
                <span className="font-mono text-gray-400 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</span>
                <Badge className={
                  a.outcome === "reject" ? "bg-red-100 text-red-700" :
                  a.outcome === "error" ? "bg-orange-100 text-orange-700" :
                  a.outcome === "skip" ? "bg-gray-100 text-gray-600" :
                  "bg-green-100 text-green-700"
                }>{a.kind}</Badge>
                <span className="text-gray-600 truncate">
                  {a.context?.reason || a.context?.symbol || a.context?.rule || JSON.stringify(a.context)?.slice(0, 80)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
